<script setup lang="ts" generic="T extends boolean">
import type { KaVeeFormFieldEmitContract } from '../KaVeeFormField';
import KaInputNumberBase from './KaInputNumberBase.vue';
import type { KaInputNumberProps } from './types';
export type { KaInputNumberBaseProps, KaInputNumberProps } from './types';

const { useNativeEvents = false, ...props } = defineProps<KaInputNumberProps<T>>();

const emit = defineEmits<
  KaVeeFormFieldEmitContract<T extends true ? Event : number> & {
    (e: 'update:modelValue', payload: number): void;
  }
>();

const convertInputValueToValue = (inputValue: string) => {
  return inputValue === '' ? NaN : parseFloat(inputValue);
};

const handleInputEvent = (event: Event) => {
  if (useNativeEvents) {
    emit('input', event as T extends true ? Event : number);
  } else {
    event.stopPropagation();
    if (!event || !(event instanceof InputEvent)) return;
    const numeric = convertInputValueToValue((event.target as HTMLInputElement).value);
    if (isNaN(numeric)) return;
    emit('input', numeric as T extends true ? Event : number);
  }
};

const handleChangeEvent = (event: Event) => {
  if (useNativeEvents) {
    emit('change', event as T extends true ? Event : number);
  } else {
    event.stopPropagation();
    if (!(event.target instanceof HTMLInputElement)) return;
    const numeric = convertInputValueToValue(event.target.value);
    if (!isNaN(numeric)) emit('change', numeric as T extends true ? Event : number);
  }
};

const handleUpdateModelValueEvent = (value: number) => {
  emit('update:modelValue', value);
  emit('change', value as any); // we break typescript here, but it's for manipulating with increment/decrement buttons to work with vee-validate
};
</script>

<template>
  <KaInputNumberBase
    v-bind="props"
    @input="handleInputEvent($event)"
    @blur="emit('blur', $event)"
    @change="handleChangeEvent($event)"
    @update:modelValue="handleUpdateModelValueEvent"
  >
    <template #label="slotProps">
      <slot name="label" v-bind="slotProps" />
    </template>
    <template #description="slotProps">
      <slot name="description" v-bind="slotProps" />
    </template>
  </KaInputNumberBase>
</template>
